import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { fetchUnseenCounts, markActivitySeen, type UnseenCounts, type ActivityType } from '../lib/coach-notifications';

const EMPTY: UnseenCounts = { progress: 0, respondents: 0, reports: 0, programs: 0 };

export function useCoachNotifications() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<UnseenCounts>(EMPTY);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const c = await fetchUnseenCounts(user.id);
      setCounts(c);
    } catch {
      setCounts(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;

    const handleChange = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(refresh, 500);
    };

    const channel = supabase
      .channel('coach-notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'goals' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tasks' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'check_ins' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'journal_entries' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'milestones' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'habit_completions' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'results' }, handleChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'respondents' }, handleChange)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'program_assignments' }, handleChange)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [user, refresh]);

  const markSeen = useCallback(async (activityType: ActivityType) => {
    if (!user) return;
    setCounts(prev => ({ ...prev, [activityType]: 0 }));
    await markActivitySeen(user.id, activityType);
    refresh();
  }, [user, refresh]);

  return { counts, loading, markSeen, refresh };
}
